import { DateTime } from "luxon";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { DialogCheckboxDescriptor } from "../../../../components/UIToolkit/Popups/Dialog";
import { useHospitalData } from "../../../../contexts/HospitalDataProvider";
import { useDeactivationImpact } from "../../../../hooks/useDeactivationImpact";
import type { Nurse } from "../../../../utils/models";

/**
 * Stable, never-translated identities for the deactivation checkboxes. Callers match on these in
 * `SystemDialog`'s `onConfirm` payload — the labels are translated and interpolated with the
 * employee's name, so they cannot double as keys.
 */
export const DEACTIVATE_POST_SHIFTS_CHECKBOX_ID = "deactivate.postShifts";
export const DEACTIVATE_FUTURE_SHIFTS_ACK_ID = "deactivate.ack.futureShifts";
export const DEACTIVATE_RECURRING_ACK_ID = "deactivate.ack.recurringSchedule";

/**
 * "Post the freed shifts" starts unticked for both a single employee and a batch: schedulers opt in
 * to reposting a deactivated nurse's deleted shifts rather than having it happen automatically. The
 * two live here together because the difference is a decision, not a drift between two files.
 *
 * Unticked matters most for a batch, where a single tick can post several hundred open shifts and
 * notify every eligible nurse about each of them — a scale at which "I didn't realise it would do
 * that" is unrecoverable. Anything reading one of these must read the other, so changing one alone
 * can't quietly diverge.
 */
export const DEACTIVATE_POST_SHIFTS_DEFAULT_SINGLE = false;
export const DEACTIVATE_POST_SHIFTS_DEFAULT_BULK = false;

const useNurseFeatureData = (nurse: Pick<Nurse, "id" | "firstName" | "departmentScheduledDeactivationDates"> | undefined) => {
	const { t } = useTranslation();
	const { chosenDepartment } = useHospitalData();
	const deptDeactivationDateStr = nurse?.departmentScheduledDeactivationDates?.[chosenDepartment.department_id] ?? null;
	const [deactivationDate, setDeactivationDate] = useState<string>(deptDeactivationDateStr ?? "");
	// DEFAULT: no date chosen means deactivating now, which removes shifts from today.
	const dateToCheck = deactivationDate || DateTime.now().setZone(chosenDepartment.timezone).toFormat("yyyy-MM-dd");
	const { impact, isChecking, isError } = useDeactivationImpact(chosenDepartment.department_id, nurse?.id, dateToCheck);

	const deactivateNursesCheckboxes: DialogCheckboxDescriptor[] = useMemo(() => {
		const checkboxes: DialogCheckboxDescriptor[] = [];
		if (!nurse) {
			return checkboxes;
		}
		if (impact?.hasFutureShifts) {
			checkboxes.push({
				id: DEACTIVATE_FUTURE_SHIFTS_ACK_ID,
				label: t("team.deactivate.ack.future_shifts", { name: nurse.firstName }),
				required: true,
				defaultValue: false,
			});
		}

		if (impact?.hasFutureRecurringShifts) {
			checkboxes.push({
				id: DEACTIVATE_RECURRING_ACK_ID,
				label: t("team.deactivate.ack.recurring_schedule", { name: nurse.firstName }),
				required: true,
				defaultValue: false,
			});
		}

		checkboxes.push({
			id: DEACTIVATE_POST_SHIFTS_CHECKBOX_ID,
			label: t("team.deactivate.post_shifts.label"),
			helperText: t("team.deactivate.post_shifts.helper"),
			required: false,
			defaultValue: DEACTIVATE_POST_SHIFTS_DEFAULT_SINGLE,
		});

		return checkboxes;
	}, [nurse, impact, t]);

	// Until the server has answered, the required "I understand" boxes may be missing; confirming then would
	// deactivate without the warning, so the dialogs hold the confirm button with this reason.
	const deactivationBlockedReason = useMemo<string | null>(() => {
		if (isChecking) return t("team.deactivate.checking_shifts");
		if (isError) return t("team.deactivate.check_failed");
		return null;
	}, [isChecking, isError, t]);

	return {
		deactivateNursesCheckboxes,
		deactivationBlockedReason,
		deactivationDate,
		setDeactivationDate,
	};
};

export default useNurseFeatureData;
